import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../common/LanguageSwitcher';
import './BusinessPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const BusinessPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('landing');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [loginSectionType, setLoginSectionType] = useState(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const loginSectionRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (loginSectionType && loginSectionRef.current) {
      loginSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loginSectionType]);

  const scrollToSection = (id) => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleShowLogin = (type) => {
    setLoginSectionType(type);
    setLoginError('');
  };

  const handleBusinessLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    if (!loginEmail.trim() || !loginPassword) {
      setLoginError(t('business.login.errorRequired'));
      return;
    }
    setLoginLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: (loginEmail || '').trim().toLowerCase(), password: loginPassword })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setLoginError(data.message || 'Invalid email or password');
        setLoginLoading(false);
        return;
      }
      if (data.token && data.user) {
        localStorage.setItem('token', data.token);
        sessionStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify({ ...data.user, role: data.role || data.user?.role }));
        if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
        const role = (data.role || data.user?.role || '').toLowerCase();
        if (loginSectionType === 'partner') {
          if (role === 'school') {
            navigate('/school-dashboard');
          } else {
            setLoginError(t('business.login.errorPartnerOnly'));
          }
        } else if (role === 'school_admin') {
          navigate('/business/dashboard');
        } else {
          setLoginError(t('business.login.errorSchoolOnly'));
        }
      } else {
        setLoginError(t('business.login.errorInvalidResponse'));
      }
    } catch (err) {
      setLoginError(err.message || 'Network error. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const schoolTypes = [
    {
      key: 'support',
      icon: 'groups',
      path: '/business/signup/support',
      cardClass: 'business-card support'
    },
    {
      key: 'accredited',
      icon: 'account_balance',
      path: '/business/signup/accredited',
      cardClass: 'business-card accredited'
    },
    {
      key: 'partner',
      icon: 'handshake',
      path: '/business/signup/partner',
      cardClass: 'business-card partner'
    }
  ];

  return (
    <div className="business-page">
      <nav className={`landing-navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="navbar-container">
          <div className="logo" onClick={() => navigate('/')}>
            <span className="material-icons">school</span>
            <span>Syllera</span>
          </div>
          <div className={`nav-menu ${isMobileMenuOpen ? 'open' : ''}`}>
            <a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection('features'); setIsMobileMenuOpen(false); }}>{t('nav.features')}</a>
            <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollToSection('how-it-works'); setIsMobileMenuOpen(false); }}>{t('nav.howItWorks')}</a>
            <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); setIsMobileMenuOpen(false); }}>{t('nav.pricing')}</a>
            <a href="#testimonials" onClick={(e) => { e.preventDefault(); scrollToSection('testimonials'); setIsMobileMenuOpen(false); }}>{t('nav.reviews')}</a>
            <a href="/resources" onClick={(e) => { e.preventDefault(); navigate('/resources'); setIsMobileMenuOpen(false); }}>{t('nav.resources')}</a>
            <a href="/business" onClick={(e) => { e.preventDefault(); navigate('/business'); setIsMobileMenuOpen(false); }} className="active">{t('nav.business')}</a>
          </div>
          <div className="nav-actions">
            <LanguageSwitcher className="landing-language-switcher" />
            <button className="btn-login" onClick={() => scrollToSection('login')}>{t('nav.login')}</button>
            <button className="btn-primary" onClick={() => navigate('/signup')}>{t('nav.startLearning')}</button>
            <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
              <span className="material-icons">{isMobileMenuOpen ? 'close' : 'menu'}</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="business-container" style={{ paddingTop: '80px' }}>
        <div className="business-header">
          <h1 className="business-title">
            <span className="material-icons">business_center</span>
            {t('business.title')}
          </h1>
          <p className="business-subtitle">
            {t('business.subtitle')}
          </p>
        </div>

        <div className="business-cards">
          {schoolTypes.map((item) => (
            <div key={item.key} className={item.cardClass}>
              <span className="material-icons business-card-icon">{item.icon}</span>
              <h3>{t(`business.${item.key}.label`)}</h3>
              <p className="business-card-fr">{t(`business.${item.key}.labelFr`)}</p>
              {item.key === 'partner' && (
                <p className="business-card-intro">{t('business.partner.intro')}</p>
              )}
              <p className="business-card-desc">{t(`business.${item.key}.description`)}</p>
              <button
                type="button"
                className="business-card-btn"
                onClick={() => navigate(item.path)}
              >
                {t(`business.${item.key}.button`)}
              </button>
              <button
                type="button"
                className="business-card-login-link"
                onClick={() => handleShowLogin(item.key)}
              >
                {t(`business.${item.key}.loginLink`)}
              </button>
            </div>
          ))}
        </div>

        {loginSectionType && (
          <section ref={loginSectionRef} className="business-login-section">
            <h2 className="business-login-title">
              {loginSectionType === 'support'
                ? t('business.login.titleSupport')
                : loginSectionType === 'accredited'
                  ? t('business.login.titleAccredited')
                  : t('business.login.titlePartner')}
            </h2>
            <form onSubmit={handleBusinessLogin} className="business-login-form">
              {loginError && (
                <div className="business-login-error">
                  <span className="material-icons">error_outline</span>
                  {loginError}
                </div>
              )}
              <label className="business-login-label">{t('business.login.email')}</label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="school@example.com"
                className="business-login-input"
                autoComplete="email"
              />
              <label className="business-login-label">{t('business.login.password')}</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="••••••••"
                className="business-login-input"
                autoComplete="current-password"
              />
              <button type="submit" className="business-login-submit" disabled={loginLoading}>
                {loginLoading ? t('business.login.submitting') : t('business.login.submit')}
              </button>
            </form>
          </section>
        )}
      </div>
    </div>
  );
};

export default BusinessPage;
